<template>
  <div class="full-cycle-plan nx-list-page">
    <div ref="extraAboveCrud" class="full-cycle-plan__above">
      <div class="metric-grid">
        <div class="metric-card">
          <span>计划总量</span>
          <strong>{{ formatWeight(summary.totalWeight) }}</strong>
          <em>吨</em>
        </div>
        <div class="metric-card metric-card--success">
          <span>已执行量</span>
          <strong>{{ formatWeight(summary.executeWeight) }}</strong>
          <em>完成率 {{ formatPercent(summary.completeRate) }}</em>
        </div>
        <div class="metric-card">
          <span>剩余计划量</span>
          <strong>{{ formatWeight(summary.remainWeight) }}</strong>
          <em>吨</em>
        </div>
        <div class="metric-card metric-card--warning">
          <span>滞后计划</span>
          <strong>{{ summary.lagCount }}</strong>
          <em>进度偏差低于 -5%</em>
        </div>
        <div class="metric-card metric-card--danger">
          <span>高风险计划</span>
          <strong>{{ summary.highRiskCount }}</strong>
          <em>逾期或严重滞后</em>
        </div>
      </div>
    </div>

    <div class="plan-crud-panel">
      <div class="plan-crud-panel__header">
        <el-tabs v-model="activePlanType" class="plan-tabs" @tab-click="handlePlanTypeChange">
          <el-tab-pane label="年计划" name="0" />
          <el-tab-pane label="月计划" name="1" />
        </el-tabs>
      </div>

      <div class="plan-crud-panel__body">
        <avue-crud
          ref="crud"
          v-model="form"
          :option="option"
          :table-loading="loading"
          :data="data"
          :page.sync="page"
          :search.sync="query"
          :defaults.sync="defaults"
          :permission="permissionList"
          :before-open="beforeOpen"
          @row-save="rowSave"
          @row-update="rowUpdate"
          @row-del="rowDel"
          @search-change="searchChange"
          @search-reset="searchReset"
          @selection-change="selectionChange"
          @refresh-change="refreshChange"
          @current-change="currentChange"
          @size-change="sizeChange"
          @on-load="onLoad"
        >
          <template slot="menuLeft">
            <el-button
              type="primary"
              size="small"
              plain
              icon="el-icon-download"
              :loading="exportLoading"
              @click="handleExport"
            >
              导出
            </el-button>
          </template>
          <table-column-set :THIS_TO_PARENT="this" slot="menuRight" />

          <template slot-scope="scope" slot="planTotalWeight">
            <span>{{ formatWeight(scope.row.planTotalWeight) }}</span>
          </template>
          <template slot-scope="scope" slot="planExecuteWeight">
            <span>{{ formatWeight(scope.row.planExecuteWeight) }}</span>
          </template>
          <template slot-scope="scope" slot="remainWeight">
            <span>{{ formatWeight(scope.row.remainWeight) }}</span>
          </template>
          <template slot-scope="scope" slot="completeRate">
            <span>{{ formatPercent(scope.row.completeRate) }}</span>
          </template>
          <template slot-scope="scope" slot="timeProgress">
            <span>{{ formatPercent(scope.row.timeProgress) }}</span>
          </template>
          <template slot-scope="scope" slot="progressDeviation">
            <span :class="['deviation-text', getDeviationClass(scope.row.progressDeviation)]">
              {{ formatSignedPercent(scope.row.progressDeviation) }}
            </span>
          </template>
          <template slot-scope="scope" slot="riskStatus">
            <el-tag size="mini" :type="getRiskTagType(scope.row.riskLevel)">
              {{ scope.row.riskText }}
            </el-tag>
          </template>
          <template slot-scope="scope" slot="auditFlag">
            <el-tag size="mini" :type="scope.row.auditFlag == 2 ? 'success' : 'warning'">
              {{ getAuditText(scope.row.auditFlag) }}
            </el-tag>
          </template>
          <template slot-scope="scope" slot="planState">
            <matrix-tag
              v-if="scope.row.planState == 0"
              color="#15803D"
              text="进行中"
            />
            <matrix-tag
              v-else-if="scope.row.planState == 1"
              color="#374151"
              text="已完成"
            />
            <matrix-tag
              v-else
              color="#909399"
              :text="getPlanStateText(scope.row.planState)"
            />
          </template>
          <template slot="menu" slot-scope="{ row }">
            <el-button
              v-if="activePlanType === '0'"
              type="text"
              size="small"
              @click="openMonthDrawer(row)"
            >
              月计划明细
            </el-button>
          </template>
        </avue-crud>
      </div>
    </div>

    <el-drawer
      title="月计划明细"
      :visible.sync="monthDrawerVisible"
      size="760px"
      custom-class="month-plan-drawer"
      append-to-body
    >
      <div v-loading="monthDrawerLoading" class="month-drawer">
        <div class="drawer-plan-info">
          <h3>{{ currentYearPlan.planName || '-' }}</h3>
          <p>
            合同：{{ currentYearPlan.contractName || '-' }}
            <span>物资：{{ currentYearPlan.cargoName || '-' }}</span>
            <span>周期：{{ formatPeriod(currentYearPlan) }}</span>
          </p>
        </div>

        <div class="drawer-metrics">
          <div>
            <span>年度计划量</span>
            <strong>{{ formatWeight(currentYearPlan.planTotalWeight) }}</strong>
          </div>
          <div>
            <span>年度执行量</span>
            <strong>{{ formatWeight(currentYearPlan.planExecuteWeight) }}</strong>
          </div>
          <div>
            <span>年度完成率</span>
            <strong>{{ formatPercent(currentYearPlan.completeRate) }}</strong>
          </div>
          <div>
            <span>滞后月份</span>
            <strong>{{ monthSummary.lagCount }}</strong>
          </div>
        </div>

        <el-table :data="monthPlanList" border size="small" class="month-table">
          <el-table-column label="月份" min-width="100">
            <template slot-scope="{ row }">
              {{ getMonthLabel(row.intervalStartTime) }}
            </template>
          </el-table-column>
          <el-table-column label="计划量(吨)" prop="planTotalWeight" align="right" min-width="110">
            <template slot-scope="{ row }">{{ formatWeight(row.planTotalWeight) }}</template>
          </el-table-column>
          <el-table-column label="执行量(吨)" prop="planExecuteWeight" align="right" min-width="110">
            <template slot-scope="{ row }">{{ formatWeight(row.planExecuteWeight) }}</template>
          </el-table-column>
          <el-table-column label="剩余量(吨)" prop="remainWeight" align="right" min-width="110">
            <template slot-scope="{ row }">{{ formatWeight(row.remainWeight) }}</template>
          </el-table-column>
          <el-table-column label="完成进度" min-width="160">
            <template slot-scope="{ row }">
              <el-progress
                :percentage="getProgressPercent(row.completeRate)"
                :stroke-width="8"
                :color="getProgressColor(row.completeRate)"
              />
            </template>
          </el-table-column>
          <el-table-column label="风险" min-width="90">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="getRiskTagType(row.riskLevel)">
                {{ row.riskText }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="审核状态" min-width="90">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.auditFlag == 2 ? 'success' : 'warning'">
                {{ getAuditText(row.auditFlag) }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>

    <select-contract ref="selectContract" @replace="replace" />
  </div>
</template>

<script>
import matrixTag from '@/components/matrix/matrix-tag'
import crudMixins from "@/mixins/crud-mixins";
import { getList, add, update, remove, getDetail, exportFullCycleExcel } from "./api";
import { rulesInput } from "@/basic-common/util/common";
import { downloadXls } from '@/util/util'
import validator from "@/util/validate";
import SelectContract from "@/views/railway-management/transportplan/components/select-contract.vue";

const BUSINESS_TYPE_TRUCK_OUT = "2";
const PLAN_TYPE_YEAR = "0";
const PLAN_TYPE_MONTH = "1";

export default {
  name: "automobile-planned-maintenance",
  mixins: [crudMixins],
  components: {
    matrixTag,
    SelectContract
  },
  data () {
    const that = this
    const currentYear = String(new Date().getFullYear())
    return {
      activePlanType: PLAN_TYPE_YEAR,
      exportLoading: false,
      monthDrawerVisible: false,
      monthDrawerLoading: false,
      monthPlanList: [],
      currentYearPlan: {},
      apiConfig: {},
      defaultParams: {
        businessType: BUSINESS_TYPE_TRUCK_OUT,
        planType: PLAN_TYPE_YEAR,
      },
      option: {
        ...this.$store.state.publicOption,
        addBtn: false,
        editBtn: false,
        delBtn: false,
        searchLabelWidth: 90,
        searchMenuSpanSuper: 6,
        menu: {
          width: 210,
          fixed: "right",
          align: "center",
        },
        column: [
          {
            label: '统计年度',
            prop: 'statisticYear',
            type: 'year',
            search: true,
            searchSpan: 6,
            hide: true,
            showColumn: false,
            addDisplay: false,
            editDisplay: false,
            viewDisplay: false,
            format: 'yyyy',
            valueFormat: 'yyyy',
            searchValue: currentYear,
            clearable: false,
          },
          {
            label: '统计月份',
            prop: 'statisticMonth',
            type: 'month',
            search: true,
            searchSpan: 6,
            hide: true,
            showColumn: false,
            addDisplay: false,
            editDisplay: false,
            viewDisplay: false,
            format: 'yyyy-MM',
            valueFormat: 'yyyy-MM',
            clearable: true,
          },
          {
            label: '计划名称',
            prop: 'planName',
            minWidth: 180,
            overHidden: true,
            maxlength: 32,
            rules: [
              { required: true, message: '请输入计划名称', trigger: 'blur' },
            ],
          },
          {
            label: '计划编号',
            prop: 'planNo',
            minWidth: 150,
            overHidden: true,
            addDisplay: false,
            editDisplay: false,
          },
          {
            label: '合同名称',
            prop: 'contractName',
            search: true,
            searchSpan: 6,
            overHidden: true,
            minWidth: 150,
            type: 'input',
            remote: true,
            readonly: true,
            rules: rulesInput(2),
            click: () => {
              const { dialogForm } = this.$refs.crud.$refs
              if (dialogForm.boxVisible) {
                this.$refs.selectContract.open(BUSINESS_TYPE_TRUCK_OUT)
              }
            }
          },
          {
            label: '客户',
            prop: 'custNo',
            overHidden: true,
            type: 'select',
            dicUrl: '/api/produce/blade-dept/select/distinct/select',
            props: {
              label: 'deptName',
              value: 'id'
            },
            width: 150,
            search: true,
            searchSpan: 6,
          },
          {
            label: '煤种',
            prop: 'cargoNo',
            type: 'select',
            remote: true,
            search: true,
            searchSpan: 6,
            dicUrl: '/api/produce/vardict/select/distinct/pycode?varName={{key}}',
            props: {
              label: 'varName',
              value: 'varNo'
            },
            width: 150,
          },
          {
            label: '物资',
            prop: 'cargoName',
            minWidth: 120,
            addDisplay: false,
            editDisplay: false,
          },
          {
            label: '计划开始时间',
            prop: 'intervalStartTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            minWidth: 165,
            pickerOptions: {
              disabledDate (time) {
                const now = new Date();
                const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                return time < currentMonthStart || time >= nextMonthStart;
              }
            },
            rules: rulesInput(2),
          },
          {
            label: '计划结束时间',
            prop: 'intervalEndTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            minWidth: 165,
            pickerOptions: {
              disabledDate (time) {
                const formData = that.form;
                const startTime = formData.intervalStartTime
                  ? new Date(formData.intervalStartTime).getTime() : 0;
                const now = new Date();
                const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                return (
                  time < currentMonthStart ||
                  time >= nextMonthStart ||
                  (startTime && time.getTime() < startTime)
                );
              }
            },
            rules: rulesInput(2),
          },
          {
            label: '计划量(吨)',
            prop: 'planTotalWeight',
            type: 'number',
            precision: 2,
            min: 0.01,
            max: 99999,
            slot: true,
            minWidth: 120,
            maxlength: 30,
            rules: rulesInput(1),
          },
          {
            label: '执行量(吨)',
            prop: 'planExecuteWeight',
            slot: true,
            minWidth: 120,
            maxlength: 12,
            rules: [
              { required: false, trigger: "blur", validator: validator.positiveNum2 }
            ]
          },
          {
            label: '剩余量(吨)',
            prop: 'remainWeight',
            slot: true,
            minWidth: 120,
            addDisplay: false,
            editDisplay: false,
          },
          {
            label: '完成进度',
            prop: 'completeRate',
            slot: true,
            minWidth: 170,
            addDisplay: false,
            editDisplay: false,
          },
          {
            label: '时间进度',
            prop: 'timeProgress',
            slot: true,
            minWidth: 170,
            addDisplay: false,
            editDisplay: false,
          },
          {
            label: '进度偏差',
            prop: 'progressDeviation',
            slot: true,
            width: 100,
            addDisplay: false,
            editDisplay: false,
          },
          {
            label: '风险状态',
            prop: 'riskStatus',
            slot: true,
            width: 100,
            addDisplay: false,
            editDisplay: false,
          },
          {
            label: '计划状态',
            prop: 'planState',
            type: 'select',
            dataType: 'string',
            dicUrl: '/api/blade-system/dict-biz/dictionary?code=planState',
            props: {
              label: 'dictValue',
              value: 'dictKey'
            },
            search: true,
            searchSpan: 6,
            slot: true,
            addDisplay: false,
            editDisplay: false,
            width: 100,
          },
          {
            label: '审核状态',
            prop: 'auditFlag',
            type: 'select',
            dataType: 'string',
            dicUrl: '/api/blade-system/dict-biz/dictionary?code=audit_flag',
            props: {
              label: 'dictValue',
              value: 'dictKey'
            },
            search: true,
            searchSpan: 6,
            slot: true,
            addDisplay: false,
            editDisplay: false,
            viewDisplay: false,
            width: 110,
          },
          {
            label: '合同开始时间',
            prop: 'contractStartTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            hide: true,
            disabled: true,
            showColumn: false,
          },
          {
            label: '合同结束时间',
            prop: 'contractEndTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            hide: true,
            disabled: true,
            showColumn: false,
          },
        ]
      },
    }
  },
  computed: {
    summary () {
      const rows = this.data || []
      const totalWeight = rows.reduce((sum, row) => sum + this.toNumber(row.planTotalWeight), 0)
      const executeWeight = rows.reduce((sum, row) => sum + this.toNumber(row.planExecuteWeight), 0)
      const remainWeight = rows.reduce((sum, row) => sum + this.toNumber(row.remainWeight), 0)
      const lagCount = rows.filter(row => this.toNumber(row.progressDeviation) <= -5).length
      const highRiskCount = rows.filter(row => row.riskLevel === 'high').length
      return {
        totalWeight,
        executeWeight,
        remainWeight,
        lagCount,
        highRiskCount,
        completeRate: totalWeight > 0 ? executeWeight / totalWeight * 100 : 0,
      }
    },
    monthSummary () {
      return {
        lagCount: (this.monthPlanList || []).filter(row => this.toNumber(row.progressDeviation) <= -5).length
      }
    },
  },
  created () {
    this.apiConfig = {
      getList,
      update: (row) => update({ ...row, ...this.defaultParams }),
      remove,
      getDetail
    }
  },
  methods: {
    rowSave (row, done, loading) {
      const planType = this.defaultParams.planType
      add({ ...row, ...this.defaultParams, planType }).then(() => {
        this.selectionClear()
        this.onLoad(this.page);
        this.$message({
          type: 'success',
          message: '操作成功'
        });
        done();
      }, () => {
        loading();
      });
    },
    searchReset () {
      crudMixins.methods.searchReset.call(this)
      this.activePlanType = this.defaultParams.planType || PLAN_TYPE_YEAR
    },
    formatParams (params) {
      const result = crudMixins.methods.formatParams.call(this, params)
      const range = this.getPlanRange(result.statisticYear, result.statisticMonth)
      delete result.statisticYear
      delete result.statisticMonth
      if (range) {
        result.planRangeStartTime = range.startTime
        result.planRangeEndTime = range.endTime
      }
      return result
    },
    afterSearch () {
      this.data = (this.data || []).map(row => this.buildPlanRow(row))
    },
    replace (row) {
      const replaceKey = ['id', 'custNo', 'contractEndTime', 'contractStartTime', 'cargoName', 'cargoNo', 'contractName', 'contractNo']
      replaceKey.forEach(item => {
        if (item !== 'id') {
          this.$set(this.form, item, row[item])
        } else {
          this.$set(this.form, 'contractExtId', row.id)
        }
      });
    },
    handlePlanTypeChange () {
      this.defaultParams = {
        ...this.defaultParams,
        planType: this.activePlanType,
      }
      this.page.currentPage = 1
      this.selectionClear()
      this.onLoad(this.page, this.query)
    },
    getPlanRange (yearValue, monthValue) {
      const year = yearValue || String(new Date().getFullYear())
      if (this.activePlanType === PLAN_TYPE_MONTH && monthValue) {
        const parts = monthValue.split('-')
        const monthStart = new Date(Number(parts[0]), Number(parts[1]) - 1, 1)
        const monthEnd = new Date(Number(parts[0]), Number(parts[1]), 0)
        return {
          startTime: this.formatDateTime(monthStart, '00:00:00'),
          endTime: this.formatDateTime(monthEnd, '23:59:59'),
        }
      }
      return {
        startTime: `${year}-01-01 00:00:00`,
        endTime: `${year}-12-31 23:59:59`,
      }
    },
    buildPlanRow (row) {
      const planTotalWeight = this.toNumber(row.planTotalWeight)
      const planExecuteWeight = this.toNumber(row.planExecuteWeight)
      const remainWeight = Math.max(planTotalWeight - planExecuteWeight, 0)
      const completeRate = planTotalWeight > 0 ? planExecuteWeight / planTotalWeight * 100 : 0
      const timeProgress = this.getTimeProgress(row.intervalStartTime, row.intervalEndTime)
      const progressDeviation = completeRate - timeProgress
      const risk = this.getRisk(row.intervalEndTime, completeRate, progressDeviation)
      return {
        ...row,
        remainWeight,
        completeRate,
        timeProgress,
        progressDeviation,
        riskLevel: risk.level,
        riskText: risk.text,
      }
    },
    toNumber (value) {
      if (value === undefined || value === null || value === '') return 0
      const parsed = Number(String(value).replace(/,/g, ''))
      return Number.isNaN(parsed) ? 0 : parsed
    },
    parseDate (value) {
      if (!value) return null
      const date = new Date(String(value).replace(/-/g, '/'))
      return Number.isNaN(date.getTime()) ? null : date
    },
    formatDateTime (date, time) {
      const year = date.getFullYear()
      const month = this.pad(date.getMonth() + 1)
      const day = this.pad(date.getDate())
      return `${year}-${month}-${day} ${time}`
    },
    pad (value) {
      return String(value).padStart(2, '0')
    },
    getTimeProgress (startValue, endValue) {
      const startTime = this.parseDate(startValue)
      const endTime = this.parseDate(endValue)
      const now = new Date()
      if (!startTime || !endTime || endTime.getTime() <= startTime.getTime()) return 0
      if (now.getTime() <= startTime.getTime()) return 0
      if (now.getTime() >= endTime.getTime()) return 100
      return (now.getTime() - startTime.getTime()) / (endTime.getTime() - startTime.getTime()) * 100
    },
    getRisk (endValue, completeRate, progressDeviation) {
      const endTime = this.parseDate(endValue)
      const isOverdue = endTime && new Date().getTime() > endTime.getTime() && completeRate < 100
      if (isOverdue || progressDeviation <= -15) {
        return { level: 'high', text: '高风险' }
      }
      if (progressDeviation > -15 && progressDeviation <= -5) {
        return { level: 'medium', text: '中风险' }
      }
      return { level: 'normal', text: '正常' }
    },
    formatWeight (value) {
      return this.toNumber(value).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    formatPercent (value) {
      return `${this.toNumber(value).toFixed(2)}%`
    },
    formatSignedPercent (value) {
      const number = this.toNumber(value)
      return `${number > 0 ? '+' : ''}${number.toFixed(2)}%`
    },
    formatPeriod (row) {
      const start = row && row.intervalStartTime ? row.intervalStartTime : '-'
      const end = row && row.intervalEndTime ? row.intervalEndTime : '-'
      return `${start} 至 ${end}`
    },
    getProgressPercent (value) {
      const number = this.toNumber(value)
      if (number < 0) return 0
      return number > 100 ? 100 : Number(number.toFixed(2))
    },
    getProgressColor (value) {
      const number = this.toNumber(value)
      if (number >= 80) return '#16a34a'
      if (number >= 50) return '#d97706'
      return '#dc2626'
    },
    getCompleteText (row) {
      return this.toNumber(row.planExecuteWeight) >= this.toNumber(row.planTotalWeight) && this.toNumber(row.planTotalWeight) > 0
        ? '完成'
        : '执行中'
    },
    getDeviationClass (value) {
      const number = this.toNumber(value)
      if (number >= 0) return 'deviation-text--ok'
      if (number > -15) return 'deviation-text--warning'
      return 'deviation-text--danger'
    },
    getRiskTagType (level) {
      if (level === 'high') return 'danger'
      if (level === 'medium') return 'warning'
      return 'success'
    },
    getAuditText (value) {
      if (value == 2) return '已审核'
      if (value == 0) return '待审核'
      return '未审核'
    },
    getPlanStateText (value) {
      if (value === undefined || value === null || value === '') return '未知'
      return String(value)
    },
    getMonthLabel (value) {
      if (!value) return '-'
      return String(value).slice(0, 7)
    },
    openMonthDrawer (row) {
      const plan = this.buildPlanRow(row)
      this.currentYearPlan = plan
      this.monthDrawerVisible = true
      this.loadMonthPlans(plan)
    },
    async loadMonthPlans (plan) {
      this.monthDrawerLoading = true
      this.monthPlanList = []
      try {
        const params = {
          businessType: BUSINESS_TYPE_TRUCK_OUT,
          planType: PLAN_TYPE_MONTH,
          contractExtId: plan.contractExtId,
          page: 1,
          size: 100,
          planRangeStartTime: plan.intervalStartTime,
          planRangeEndTime: plan.intervalEndTime,
        }
        const res = await getList(params)
        const records = (((res || {}).data || {}).data || {}).records || []
        this.monthPlanList = records.map(row => this.buildPlanRow(row))
      } catch (e) {
        this.$message.error('月计划明细加载失败')
      } finally {
        this.monthDrawerLoading = false
      }
    },
    async handleExport () {
      const total = this.page.total || 0
      if (total < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      const maxRows = 10000
      if (total > maxRows) {
        this.$message.warning(`当前最多导出 ${maxRows} 条，请缩小筛选范围后重试`)
        return
      }
      this.exportLoading = true
      try {
        const params = this.formatParams(Object.assign({}, this.defaultParams, this.query))
        const res = await exportFullCycleExcel(params)
        downloadXls(res.data, `全周期发运计划_${this.getExportTimeText()}.xlsx`)
        this.$message.success('导出成功')
      } catch (e) {
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exportLoading = false
      }
    },
    getExportTimeText () {
      const date = new Date()
      return [
        date.getFullYear(),
        this.pad(date.getMonth() + 1),
        this.pad(date.getDate()),
        this.pad(date.getHours()),
        this.pad(date.getMinutes()),
        this.pad(date.getSeconds()),
      ].join('')
    },
  }
}
</script>

<style scoped lang="scss">
.full-cycle-plan {
  .full-cycle-plan__above {
    margin-bottom: 12px;
  }

  .metric-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(150px, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }

  .metric-card {
    min-height: 86px;
    padding: 14px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 6px;

    span {
      display: block;
      margin-bottom: 10px;
      color: #64748b;
      font-size: 13px;
    }

    strong {
      display: block;
      color: #111827;
      font-size: 24px;
      line-height: 28px;
      font-weight: 700;
    }

    em {
      display: block;
      margin-top: 6px;
      color: #94a3b8;
      font-style: normal;
      font-size: 12px;
    }

    &--success strong {
      color: #15803d;
    }

    &--warning strong {
      color: #b45309;
    }

    &--danger strong {
      color: #dc2626;
    }
  }

  .plan-crud-panel {
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    overflow: hidden;
  }

  .plan-crud-panel__header {
    display: flex;
    align-items: center;
    min-height: 46px;
    padding: 0 18px;
    background: #fff;
    border-bottom: 1px solid #ebeef5;
  }

  .plan-crud-panel__body {
    background: #fff;
  }

  .plan-tabs {
    width: 100%;
  }

  .deviation-text {
    font-weight: 600;

    &--ok {
      color: #15803d;
    }

    &--warning {
      color: #b45309;
    }

    &--danger {
      color: #dc2626;
    }
  }
}

.month-drawer {
  padding: 0 20px 20px;

  .drawer-plan-info {
    padding-bottom: 14px;
    border-bottom: 1px solid #ebeef5;

    h3 {
      margin: 0 0 8px;
      color: #1f2937;
      font-size: 16px;
      font-weight: 600;
    }

    p {
      margin: 0;
      color: #606266;
      line-height: 22px;
    }

    span {
      margin-left: 14px;
    }
  }

  .drawer-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin: 14px 0;

    div {
      padding: 12px;
      background: #f8fafc;
      border: 1px solid #ebeef5;
      border-radius: 6px;
    }

    span {
      display: block;
      margin-bottom: 8px;
      color: #64748b;
      font-size: 12px;
    }

    strong {
      color: #111827;
      font-size: 18px;
      font-weight: 700;
    }
  }

  .month-table {
    width: 100%;
  }
}

::v-deep .plan-tabs .el-tabs__header {
  margin: 0;
}

::v-deep .plan-tabs .el-tabs__item {
  height: 46px;
  line-height: 46px;
}

::v-deep .plan-tabs .el-tabs__nav-wrap::after {
  height: 0;
}

::v-deep .plan-crud-panel .avue-crud {
  padding: 0;
}

::v-deep .plan-crud-panel .avue-crud__search {
  margin: 0;
  padding: 0;
  background: #fff;
}

::v-deep .plan-crud-panel .avue-crud__search > .el-card__body {
  padding: 12px 16px 10px !important;
  box-sizing: border-box;
}

::v-deep .plan-crud-panel .avue-crud__search .avue-group--header {
  padding: 10px 5px 3px;
  background: #f0f7ff;
  box-sizing: border-box;
}

::v-deep .plan-crud-panel .avue-crud__menu {
  margin: 0;
  box-sizing: border-box;
}

::v-deep .plan-crud-panel .avue-crud__table {
  padding: 0 16px;
}

::v-deep .plan-crud-panel .avue-crud__pagination {
  margin: 0;
  padding: 8px 16px;
  border-top: 1px solid #ebeef5;
  box-sizing: border-box;
}

::v-deep .el-input__icon {
  line-height: 1 !important;
}

@media (max-width: 1200px) {
  .full-cycle-plan .metric-grid {
    grid-template-columns: repeat(2, minmax(150px, 1fr));
  }
}

@media (max-width: 768px) {
  .full-cycle-plan {
    .metric-grid {
      grid-template-columns: 1fr;
    }
  }

  .month-drawer .drawer-metrics {
    grid-template-columns: 1fr;
  }
}
</style>
